import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { FiUpload, FiX, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { agentAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { getAgentStatusMeta, normalizeAgentStatus } from '../../utils/agentStatus';

const NAME_PREFIXES = ['MISS', 'MRS', 'MR', 'MS', 'นาย', 'น.ส.', 'นาง', 'นางสาว'];

function stripPrefix(name) {
  if (!name) return { first: '', last: '' };
  let s = String(name).trim();
  for (const p of NAME_PREFIXES) {
    if (s.toUpperCase().startsWith(p.toUpperCase())) {
      s = s.slice(p.length).trim();
      break;
    }
  }
  const parts = s.split(/\s+/);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') };
}

// Official agency format: month-first dates with a 2-digit Buddhist-era year.
// e.g. "10/18/65" = 18 Oct 2565 BE = 2022 CE ; "10/17/73" = 2030 CE ; "11/29/25" = 1982.
function parseBEDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val.toISOString().slice(0, 10);
  const s = String(val).trim();
  // Excel serial number (fallback if a cell comes through as a real date)
  if (/^\d{5,}$/.test(s)) {
    const d = XLSX.SSF.parse_date_code(parseInt(s, 10));
    if (!d) return null;
    return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
  }
  // M/D/YY or M/D/YYYY (month-first)
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    const month = +m[1];
    const day = +m[2];
    let year = +m[3];
    if (year < 100) year = 2500 + year; // 2-digit → 25YY (BE)
    if (year > 2400) year -= 543;        // BE → CE
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return null;
}

// Keys are built by combining the two header rows (row0 + " " + row1).trim().
// Unmatched columns (สังกัด, รหัส 9 หลัก, ผู้บริหารต้นสังกัด, วันที่สถานะภาพ, เลขผู้เสียภาษี,
// วันที่ได้รับตำแหน่ง) are intentionally absent → discarded automatically.
const COL_MAP_V2 = {
  'รหัสตัวแทน': 'agentCode',
  'รหัสหน่วย': 'unitCode',
  'ชื่อ-นามสกุล (ไทย)': '_nameThai',
  'ชื่อ-นามสกุล (อังกฤษ)': '_nameEn',
  'ตำแหน่ง': 'position',
  'วันที่ตำแหน่ง มีผลบังคับ': 'positionEffectiveDate',
  'วันที่เริ่มสัญญา ตัวแทน': 'joinDate',
  'เลขที่ใบอนุญาต ตัวแทน': 'licenseNumber',
  'วันที่ใบอนุญาต ตัวแทน': 'licenseStartDate',
  'วันที่ใบอนุญาต ตัวแทนสิ้นผลบังคับ': 'licenseExpiryDate',
  'สถานภาพการขาย': 'licenseLevel', // closest existing field (e.g. "จูเนียร์")
  'สถานภาพตัวแทน': 'agentStatus',
  'วัน เดือน ปีเกิด': 'birthDate',
  'อีเมล์': 'email',
};

const DATE_FIELDS = ['positionEffectiveDate', 'joinDate', 'licenseStartDate', 'licenseExpiryDate', 'birthDate'];
const STRING_FIELDS = ['agentCode', 'unitCode', 'position', 'licenseNumber', 'licenseLevel', 'agentStatus', 'email'];

function rowToAgent(row, headers) {
  const raw = {};
  headers.forEach((h, i) => {
    const key = COL_MAP_V2[h];
    if (key) raw[key] = row[i];
  });

  const agent = {};

  if (raw._nameThai) {
    const { first, last } = stripPrefix(raw._nameThai);
    agent.firstName = first;
    agent.lastName = last;
  }

  if (raw._nameEn) {
    const { first, last } = stripPrefix(raw._nameEn);
    agent.firstNameEn = first;
    agent.lastNameEn = last;
  }

  STRING_FIELDS.forEach((k) => {
    if (raw[k] !== undefined && raw[k] !== null && raw[k] !== '') {
      agent[k] = String(raw[k]).trim();
    }
  });

  agent.agentStatus = normalizeAgentStatus(raw.agentStatus);

  DATE_FIELDS.forEach((k) => {
    if (raw[k] !== undefined && raw[k] !== null && raw[k] !== '') {
      const parsed = parseBEDate(raw[k]);
      if (parsed) agent[k] = parsed;
    }
  });

  return agent;
}

export default function ImportAgentsModalV2({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [fileName, setFileName] = useState('');
  const [agentRows, setAgentRows] = useState([]);
  const [parseWarnings, setParseWarnings] = useState([]);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const reset = () => {
    setStep('upload');
    setFileName('');
    setAgentRows([]);
    setParseWarnings([]);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        // raw:false keeps dates as their displayed text so the 2-digit BE year survives.
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });

        if (rows.length < 3) {
          toast.error('ไฟล์ต้องมีอย่างน้อย 3 แถว (หัวตาราง 2 แถว + ข้อมูล)');
          return;
        }

        // Row 1 (index 0) + Row 2 (index 1) = combined headers ; data starts at index 2.
        const row0 = rows[0];
        const row1 = rows[1];
        const colCount = Math.max(row0.length, row1.length);
        const headers = [];
        for (let i = 0; i < colCount; i++) {
          const a = String(row0[i] ?? '').trim();
          const b = String(row1[i] ?? '').trim();
          headers.push(`${a} ${b}`.trim());
        }

        const warnings = [];
        const agents = [];
        for (let i = 2; i < rows.length; i++) {
          const row = rows[i];
          if (row.every((c) => c === '' || c === null || c === undefined)) continue; // skip blank rows
          const a = rowToAgent(row, headers);
          if (!a.agentCode) {
            warnings.push(`แถว ${i + 1}: ไม่พบรหัสตัวแทน — ข้ามแถวนี้`);
            continue;
          }
          agents.push(a);
        }

        setAgentRows(agents);
        setParseWarnings(warnings);
        setStep('preview');
      } catch (err) {
        toast.error('ไม่สามารถอ่านไฟล์ได้: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    setStep('importing');
    try {
      const { data } = await agentAPI.importBulk({ agentsData: agentRows });
      setResult(data);
      setStep('done');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error('การนำเข้าล้มเหลว: ' + (err.response?.data?.message || err.message));
      setStep('preview');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">นำเข้าข้อมูล (รูปแบบทางการ)</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Upload Step */}
          {step === 'upload' && (
            <div className="flex flex-col items-center justify-center py-10 space-y-4">
              <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiUpload} className="w-8 h-8 text-teal-600" />
              </div>
              <div className="text-center">
                <p className="text-gray-700 font-medium">เลือกไฟล์ Excel (.xlsx) รูปแบบทางการ</p>
                <p className="text-gray-400 text-sm mt-1">แถวที่ 1–2 = หัวตาราง (ข้าม) · แถวที่ 3+ = ข้อมูลตัวแทน</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFile}
                className="hidden"
                id="xlsx-input-v2"
              />
              <label
                htmlFor="xlsx-input-v2"
                className="cursor-pointer px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
              >
                เลือกไฟล์
              </label>
            </div>
          )}

          {/* Preview Step */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                <span className="font-medium">ไฟล์:</span> {fileName}
              </div>

              {parseWarnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-1">
                  <div className="flex items-center text-yellow-700 font-medium text-sm">
                    <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1.5" />
                    คำเตือน ({parseWarnings.length})
                  </div>
                  {parseWarnings.map((w, i) => (
                    <p key={i} className="text-yellow-600 text-xs pl-5">{w}</p>
                  ))}
                </div>
              )}

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">ตัวแทนที่จะนำเข้า ({agentRows.length} รายการ)</p>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-60">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 text-gray-500 font-medium">#</th>
                          <th className="text-left px-3 py-2 text-gray-500 font-medium">รหัส</th>
                          <th className="text-left px-3 py-2 text-gray-500 font-medium">ชื่อ-นามสกุล</th>
                          <th className="text-left px-3 py-2 text-gray-500 font-medium">ตำแหน่ง</th>
                          <th className="text-left px-3 py-2 text-gray-500 font-medium">สถานภาพ</th>
                          <th className="text-left px-3 py-2 text-gray-500 font-medium">วันหมดอายุใบอนุญาต</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {agentRows.map((a, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                            <td className="px-3 py-2 font-medium text-gray-700">{a.agentCode}</td>
                            <td className="px-3 py-2 text-gray-700">{a.firstName} {a.lastName}</td>
                            <td className="px-3 py-2 text-gray-500 truncate max-w-[120px]">{a.position || '-'}</td>
                            <td className="px-3 py-2 text-gray-500 truncate max-w-[180px]">{getAgentStatusMeta(a.agentStatus).label}</td>
                            <td className="px-3 py-2 text-gray-500">{a.licenseExpiryDate || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Importing Step */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-10 space-y-4">
              <div className="w-14 h-14 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-600 font-medium">กำลังนำเข้าข้อมูล...</p>
            </div>
          )}

          {/* Done Step */}
          {step === 'done' && result && (
            <div className="flex flex-col items-center justify-center py-10 space-y-4">
              <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiCheckCircle} className="w-9 h-9 text-teal-600" />
              </div>
              <div className="text-center space-y-1">
                <p className="text-gray-900 font-semibold text-lg">นำเข้าข้อมูลสำเร็จ!</p>
                <p className="text-gray-500 text-sm">เพิ่มใหม่ <span className="font-bold text-teal-600">{result.imported}</span> รายการ · อัปเดต <span className="font-bold text-indigo-600">{result.updated}</span> รายการ</p>
              </div>
              {result.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 w-full space-y-1">
                  <p className="text-red-600 font-medium text-sm">ข้อผิดพลาด ({result.errors.length})</p>
                  {result.errors.map((e, i) => <p key={i} className="text-red-500 text-xs">{e}</p>)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end space-x-3">
          {step === 'upload' && (
            <button onClick={handleClose} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium">
              ยกเลิก
            </button>
          )}

          {step === 'preview' && (
            <>
              <button onClick={() => { reset(); }} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium border border-gray-300 rounded-lg">
                เลือกไฟล์ใหม่
              </button>
              <button
                onClick={handleImport}
                disabled={agentRows.length === 0}
                className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                ยืนยันนำเข้า {agentRows.length} รายการ
              </button>
            </>
          )}

          {step === 'done' && (
            <button onClick={handleClose} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium">
              เสร็จสิ้น
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
